import { useState, useCallback, useEffect } from 'react';
import { toast } from '@/hooks/use-toast';
import { FbConversation, QueueCustomer, QueueError } from '@/types/fb-type';
import { SaleTabKey } from '../components/sale-sidebar-tabs';
import { useChat } from '@/contexts/ChatContext';
import {
  fetchAutoDetectChat,
  fetchAutoDetectComment,
  fetchCommentPoller,
  updateAutoDetectChat,
  updateAutoDetectComment,
} from '@/api/setting-api';
import { useSaleSidebarData } from '../context/sale-product-context';
import { useLiveCommentFeed } from './use-live-comment-feed';

export function useFloatingWidgets() {
  const [showingComment, setShowingCommentState] = useState(false);
  const [showingChatMessage, setShowingChatMessage] = useState(false);
  const [showingCommentsFloat, setShowingCommentsFloat] = useState(false);
  const [showingMessagingFloat, setShowingMessagingFloat] = useState(false);
  const [autoDetectComment, setAutoDetectComment] = useState(false);
  const [autoDetectChat, setAutoDetectChat] = useState(false);

  const { fbLives, fetchLiveVideos, upsertActiveLive } = useSaleSidebarData();

  const openCommentPanel = useCallback(() => setShowingCommentState(true), []);

  const {
    comments,
    fetchCommentState,
    handleSelectPost,
    isPolling,
    loadMoreComment,
    openCommentPanel: bindOpenCommentPanel,
    rebindLiveTab,
  } = useLiveCommentFeed({
    fbLives,
    fetchLiveVideos,
    upsertActiveLive,
    showingComment,
    onOpenPanel: openCommentPanel,
  });

  useEffect(() => {
    void fetchCommentPoller();
    void fetchAutoDetectChat().then((resp) => {
      if (resp.success) setAutoDetectChat(resp.data);
    });
    void fetchAutoDetectComment().then((resp) => {
      if (resp.success) setAutoDetectComment(resp.data);
    });
  }, []);

  const { selectConversation, currentConversation, reloadMessages } = useChat();

  const handleShowingCommentChange = useCallback(
    (open: boolean) => {
      setShowingCommentState(open);
      if (open) bindOpenCommentPanel();
    },
    [bindOpenCommentPanel],
  );

  const handleTabChange = useCallback(
    (tabKey: SaleTabKey) => {
      setShowingCommentState(false);
      if (tabKey === 'post' || tabKey === 'live') {
        setShowingMessagingFloat(false);
        setShowingCommentsFloat(true);
        if (tabKey === 'live') void rebindLiveTab();
      } else if (tabKey === 'inbox') {
        setShowingCommentsFloat(false);
        setShowingMessagingFloat(true);
      } else {
        setShowingChatMessage(false);
        setShowingMessagingFloat(false);
        setShowingCommentsFloat(false);
      }
    },
    [rebindLiveTab],
  );

  useEffect(() => {
    handleTabChange('live');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConversationSelect = useCallback(
    (conv: FbConversation) => {
      if (conv === currentConversation) reloadMessages(conv);
      else selectConversation(conv);
      setShowingChatMessage(true);
    },
    [currentConversation, reloadMessages, selectConversation],
  );

  const handleSelectQueueCustomer = useCallback((q: QueueCustomer) => {
    if (q.queueError === QueueError.NeedReplyMsg) {
      setShowingCommentsFloat(false);
      setShowingCommentState(false);
      setShowingMessagingFloat(true);
    } else {
      setShowingCommentState(false);
      setShowingChatMessage(false);
      setShowingCommentsFloat(true);
      setShowingMessagingFloat(false);
    }
  }, []);

  const handleUpdateAutoDetectComment = async (flag: boolean) => {
    const resp = await updateAutoDetectComment(flag);
    if (!resp.success) {
      toast({
        title: 'Error',
        content: resp.message || 'Error updating auto detect comment',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateAutoDetectChat = async (flag: boolean) => {
    const resp = await updateAutoDetectChat(flag);
    if (!resp.success) {
      toast({
        title: 'Error',
        content: resp.message || 'Error updating auto detect chat',
        variant: 'destructive',
      });
    }
  };

  return {
    showingComment,
    setShowingComment: handleShowingCommentChange,
    showingChatMessage,
    setShowingChatMessage,
    comments,
    showingCommentsFloat,
    showingMessagingFloat,
    fetchCommentState,
    handleSelectPost,
    handleTabChange,
    handleConversationSelect,
    handleSelectQueueCustomer,
    isPolling,
    loadMoreComment,
    autoDetectComment,
    autoDetectChat,
    handleUpdateAutoDetectChat,
    handleUpdateAutoDetectComment,
  };
}
